import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface PixverseV4TransitionParams {
  /**
   * 视频生成提示词，必填参数
   */
  prompt: string;
  /**
   * 首帧图像，必填参数。支持URL或Base64格式，与first_frame_image二选一
   */
  first_frame_img?: string;
  /**
   * 首帧图像，必填参数。支持URL或Base64格式，与first_frame_img二选一
   */
  first_frame_image?: string;
  /**
   * 尾帧图像，必填参数。支持URL或Base64格式，与last_frame_image二选一
   */
  last_frame_img?: string;
  /**
   * 尾帧图像，必填参数。支持URL或Base64格式，与last_frame_img二选一
   */
  last_frame_image?: string;
  /**
   * 视频时长（秒），可选5秒或8秒，默认5秒
   * @default 5
   * @values 5 | 8
   */
  duration: 5 | 8;
  /**
   * 视频质量，可选360p、540p、720p、1080p，默认540p
   * @default "540p"
   * @values 360p | 540p | 720p | 1080p
   */
  quality: '360p' | '540p' | '720p' | '1080p';
  /**
   * 运动模式，可选normal（标准）、fast（快速），默认normal
   * @default "normal"
   * @values normal | fast
   */
  motion_mode?: 'normal' | 'fast';
  /**
   * 随机种子
   */
  seed?: number;
  /**
   * 音效开关
   */
  sound_effect_switch?: boolean;
  /**
   * 音效内容
   */
  sound_effect_content?: string;
  /**
   * 唇形同步TTS开关
   */
  lip_sync_tts_switch?: boolean;
  /**
   * 唇形同步TTS内容
   */
  lip_sync_tts_content?: string;
  /**
   * 唇形同步TTS说话人ID
   */
  lip_sync_tts_speaker_id?: string;
}

export interface PixverseV4TransitionResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * pixverse_v4_transition
 *
 * API Documentation: https://docs.platform.pixverse.ai/transitionfirst-last-frame-generation-15123014e0
 * 
 * PixVerse V4 过渡（首尾帧）视频生成实现。支持通过首帧和尾帧图像生成过渡视频。
 *
 * @example
 * ```typescript
 * import { initSeacloud, pixverseV4Transition } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await pixverseV4Transition({
 *   // 参数见 PixverseV4TransitionParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 PixverseV4TransitionParams 接口定义
 * @returns 生成的资源列表
 */
export async function pixverseV4Transition(
  params: PixverseV4TransitionParams,
): Promise<PixverseV4TransitionResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'pixverse_v4_transition',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: PixverseV4TransitionResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
